import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useToast } from '@veracity/vui'
import { useNavigate } from 'react-router-dom'

import { financesClient } from '@/api'
import { config } from '@/config'
import { UpdateFinancesDatatasetRequest } from '@/interfaces'

const BASE_ROUTE = config.links.library.finances
const BASE_QUERY_KEY = financesClient.QUERY_KEY

export const useFinancesDetails = (datasetId: number) => {
  const QUERY_KEY = [...BASE_QUERY_KEY, datasetId]

  const navigate = useNavigate()
  const { showSuccess } = useToast()
  const queryClient = useQueryClient()

  const allDatasets = useQuery({
    queryKey: [...financesClient.QUERY_KEY, { lockedOnly: false }],
    queryFn: () => financesClient.getAll()
  })

  const allDatasetNames =
    allDatasets.data?.map(x => x.name || '').filter(Boolean) || []

  const datasetInfoQuery = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => financesClient.getById(datasetId),
    enabled: !!datasetId
  })

  const updateDatasetMutation = useMutation({
    mutationFn: (req: UpdateFinancesDatatasetRequest) =>
      financesClient.updateDataset(datasetId, req),
    onSuccess: async () => {
      showSuccess('Dataset successfully updated')
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY })
    }
  })

  const cloneDatasetMutation = useMutation({
    mutationFn: (datasetName: string) =>
      financesClient.cloneDataset({ datasetId, name: datasetName }),
    onSuccess: clonedDatasetId => {
      showSuccess('Dataset successfully cloned')
      navigate(`${BASE_ROUTE}/${clonedDatasetId}`)
    }
  })

  const removeDatasetMutation = useMutation({
    mutationFn: () => financesClient.removeDataset(datasetId),
    onSuccess: () => {
      showSuccess('Dataset successfully removed')
      navigate(BASE_ROUTE)
    }
  })

  const lockDatasetMutation = useMutation({
    mutationFn: () => financesClient.lockDataset(datasetId),
    onSuccess: async () => {
      showSuccess('Dataset successfully locked')
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY })
    }
  })

  const archiveDatasetMutation = useMutation({
    mutationFn: () => financesClient.archiveDataset(datasetId),
    onSuccess: async () => {
      showSuccess('Dataset successfully archived')
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY })
    }
  })

  const unArchiveDatasetMutation = useMutation({
    mutationFn: () => financesClient.unArchiveDataset(datasetId),
    onSuccess: async () => {
      showSuccess('Dataset successfully unarchived')
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY })
    }
  })

  return {
    datasetInfo: datasetInfoQuery.data,
    allDatasetNames,
    isFetching: datasetInfoQuery.isFetching,
    updateDataset: updateDatasetMutation.mutate,
    isUpdateDatasetInProgress: updateDatasetMutation.isPending,
    cloneDataset: cloneDatasetMutation.mutate,
    isCloneDatasetInProgress: cloneDatasetMutation.isPending,
    removeDataset: removeDatasetMutation.mutate,
    lockDataset: lockDatasetMutation.mutate,
    archiveDataset: archiveDatasetMutation.mutate,
    unArchiveDataset: unArchiveDatasetMutation.mutate,
    getDownloadFileUrl: financesClient.getDownloadFileUrl
  }
}
